<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';
import MetaReportsAPI from 'dashboard/api/metaReports';
import {
  fmt,
  fmtPct,
  statusBadgeClass,
  qualityBadgeClass,
  statusLabel,
  qualityLabel,
} from '../helper/metaFormatters';

const props = defineProps({
  inboxId: { type: [Number, String], default: null },
  since: { type: Number, required: true },
  until: { type: Number, required: true },
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const isLoading = ref(false);
const templates = ref([]);
const templatesPartial = ref(false);

const templateTotals = computed(() => {
  const sent = templates.value.reduce((acc, tpl) => acc + (tpl.sent || 0), 0);
  const read = templates.value.reduce((acc, tpl) => acc + (tpl.read || 0), 0);
  return { sent, read, readRate: sent > 0 ? (read / sent) * 100 : 0 };
});

const openTemplate = tpl => {
  router.push({
    name: 'meta_reports_template',
    params: { templateId: tpl.id },
    query: route.query,
  });
};

const fetchTemplates = async () => {
  if (!props.inboxId) return;
  isLoading.value = true;
  try {
    const { data } = await MetaReportsAPI.getTemplates({
      inboxId: props.inboxId,
      since: props.since,
      until: props.until,
    });
    templates.value = data.templates || [];
    templatesPartial.value = Boolean(data.partial);
  } catch (error) {
    templates.value = [];
    templatesPartial.value = false;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => [props.inboxId, props.since, props.until],
  fetchTemplates,
  { immediate: true }
);
</script>

<template>
  <div v-if="isLoading" class="flex justify-center py-10">
    <Spinner size="" />
  </div>

  <div v-else>
    <div
      v-if="templatesPartial"
      class="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg px-4 py-2 mb-4"
    >
      <fluent-icon icon="warning-outline" size="16" />
      {{ $t('META_REPORTS.TEMPLATES.PARTIAL_WARNING') }}
    </div>

    <div
      v-if="templates.length"
      class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4"
    >
      <div
        class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('META_REPORTS.TEMPLATES.SENT') }}
        </span>
        <p class="text-2xl font-semibold text-n-slate-12 mt-1">
          {{ fmt(templateTotals.sent) }}
        </p>
      </div>
      <div
        class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('META_REPORTS.TEMPLATES.READ') }}
        </span>
        <p class="text-2xl font-semibold text-n-slate-12 mt-1">
          {{ fmt(templateTotals.read) }}
        </p>
      </div>
      <div
        class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-5"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('META_REPORTS.TEMPLATES.READ_RATE') }}
        </span>
        <p class="text-2xl font-semibold text-n-slate-12 mt-1">
          {{ fmtPct(templateTotals.readRate) }}
        </p>
      </div>
    </div>

    <div
      v-if="!templates.length"
      class="flex flex-col items-center justify-center py-12 text-center border border-dashed border-n-weak rounded-lg"
    >
      <fluent-icon icon="document-outline" size="32" class="text-n-slate-9 mb-3" />
      <p class="text-n-slate-12 font-medium">
        {{ $t('META_REPORTS.TEMPLATES.EMPTY_TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11 mt-1 max-w-md">
        {{ $t('META_REPORTS.TEMPLATES.EMPTY_BODY') }}
      </p>
    </div>

    <div
      v-else
      class="border border-n-weak rounded-lg overflow-y-auto max-h-[32rem]"
    >
      <table class="w-full text-sm border-collapse">
        <thead
          class="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-n-weak"
        >
          <tr class="text-xs font-medium uppercase tracking-wide text-n-slate-11">
            <th class="text-left py-2.5 px-4">
              {{ $t('META_REPORTS.TEMPLATES.NAME') }}
            </th>
            <th class="text-left py-2.5 px-3 whitespace-nowrap">
              {{ $t('META_REPORTS.TEMPLATES.CATEGORY') }}
            </th>
            <th class="text-left py-2.5 px-3">
              {{ $t('META_REPORTS.TEMPLATES.LANGUAGE') }}
            </th>
            <th class="text-left py-2.5 px-3">
              {{ $t('META_REPORTS.TEMPLATES.STATUS') }}
            </th>
            <th class="text-left py-2.5 px-3">
              {{ $t('META_REPORTS.TEMPLATES.QUALITY') }}
            </th>
            <th class="text-right py-2.5 px-3 whitespace-nowrap">
              {{ $t('META_REPORTS.TEMPLATES.SENT') }}
            </th>
            <th class="text-right py-2.5 px-3 whitespace-nowrap">
              {{ $t('META_REPORTS.TEMPLATES.DELIVERED') }}
            </th>
            <th class="text-right py-2.5 px-4 whitespace-nowrap">
              {{ $t('META_REPORTS.TEMPLATES.READ') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="tpl in templates"
            :key="tpl.id"
            class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1 transition-colors cursor-pointer"
            @click="openTemplate(tpl)"
          >
            <td class="py-2.5 px-4 align-middle max-w-[16rem]">
              <span
                :title="tpl.name"
                class="block truncate font-medium text-n-blue-text"
              >
                {{ tpl.name }}
              </span>
            </td>
            <td class="py-2.5 px-3 align-middle text-n-slate-11 whitespace-nowrap">
              {{ tpl.category || '—' }}
            </td>
            <td class="py-2.5 px-3 align-middle text-n-slate-11 uppercase">
              {{ tpl.language || '—' }}
            </td>
            <td class="py-2.5 px-3 align-middle whitespace-nowrap">
              <span v-if="tpl.status" :class="statusBadgeClass(tpl.status)">
                {{ statusLabel(tpl.status, t) }}
              </span>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="py-2.5 px-3 align-middle whitespace-nowrap">
              <span :class="qualityBadgeClass(tpl.quality_score)">
                {{ qualityLabel(tpl.quality_score, t) }}
              </span>
            </td>
            <td
              class="py-2.5 px-3 align-middle text-right tabular-nums font-medium text-n-slate-12"
            >
              {{ fmt(tpl.sent) }}
            </td>
            <td
              class="py-2.5 px-3 align-middle text-right tabular-nums text-n-slate-12"
            >
              {{ fmt(tpl.delivered) }}
            </td>
            <td
              class="py-2.5 px-4 align-middle text-right tabular-nums text-n-slate-12"
            >
              {{ fmt(tpl.read) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
